// /support/ticket/:id — чат-вид одного тикета.
//
// Polling каждые 10 сек на активной вкладке, 30 сек на фоне (через
// document.visibilityState). При status='closed'/'spam' textarea
// disabled, показываем кнопку «Создать новое обращение» с авто-ссылкой
// в parent_ticket_id (фронт не использует пока — это для Этапа 5/6).
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import SupportShell from "../components/support/SupportShell";
import { useAuth } from "../context/AuthContext";
import {
  getTicket, addMessage, closeByUser,
} from "../api/tickets";
import TicketChatView from "../components/support/TicketChatView";
import TicketStatusBadge from "../components/support/TicketStatusBadge";
import TicketAttachmentUpload from "../components/support/TicketAttachmentUpload";
import { PaperAirplaneIcon } from "../components/icons/PaperAirplaneIcon";
import usePageTitle from "../hooks/usePageTitle";
import "../styles/support.css";

const POLL_ACTIVE_MS = 10_000;
const POLL_HIDDEN_MS = 30_000;

export default function SupportTicketPage() {
  usePageTitle("Обращение");
  const { id } = useParams();
  const { isAuthenticated, initializing } = useAuth();
  const navigate = useNavigate();

  const [ticket, setTicket] = useState(null);
  const [err, setErr] = useState(null);

  const [draft, setDraft] = useState("");
  const [drafts, setDrafts] = useState([]);          // attachments
  const [sending, setSending] = useState(false);
  const [sendErr, setSendErr] = useState(null);

  // Polling-таймер. Перезапускается при смене visibility.
  const pollTimerRef = useRef(null);
  const cancelledRef = useRef(false);

  const fetchTicket = useCallback(async () => {
    try {
      const t = await getTicket(id);
      if (!cancelledRef.current) setTicket(t);
    } catch (e) {
      if (!cancelledRef.current) setErr(e.message);
    }
  }, [id]);

  /* первый запрос + polling */
  useEffect(() => {
    if (initializing) return;
    if (!isAuthenticated) {
      navigate(`/login?next=/support/ticket/${id}`, { replace: true });
      return;
    }
    cancelledRef.current = false;
    fetchTicket();

    const schedule = () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
      const ms = document.visibilityState === "visible"
        ? POLL_ACTIVE_MS
        : POLL_HIDDEN_MS;
      pollTimerRef.current = setTimeout(async () => {
        if (cancelledRef.current) return;
        await fetchTicket();
        schedule();
      }, ms);
    };
    schedule();

    const onVis = () => schedule();
    document.addEventListener("visibilitychange", onVis);

    return () => {
      cancelledRef.current = true;
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [id, initializing, isAuthenticated, navigate, fetchTicket]);

  const send = async () => {
    setSendErr(null);
    const text = draft.trim();
    if (!text) return;
    setSending(true);
    try {
      await addMessage(id, {
        text,
        attachments: drafts.map((a) => a.file_id),
      });
      setDraft("");
      setDrafts([]);
      // сразу обновить ленту, не дожидаясь polling
      await fetchTicket();
    } catch (e) {
      setSendErr(e.message);
    } finally {
      setSending(false);
    }
  };

  const closeTicket = async () => {
    if (!window.confirm("Закрыть это обращение как решённое?")) return;
    try {
      await closeByUser(id);
      await fetchTicket();
    } catch (e) {
      setSendErr(e.message);
    }
  };

  if (initializing || !isAuthenticated) {
    return <SupportShell><div className="sup-page"><div className="sup-msg-info">Загрузка…</div></div></SupportShell>;
  }
  if (err) {
    return (
      <SupportShell>
        <div className="sup-page">
          <div className="sup-msg-err">Не удалось загрузить: {err}</div>
          <div style={{ marginTop: 12 }}>
            <button type="button" className="sup-btn-ghost"
                    onClick={() => navigate("/support/my")}>
              ‹ К списку обращений
            </button>
          </div>
        </div>
      </SupportShell>
    );
  }
  if (!ticket) {
    return <SupportShell><div className="sup-page"><div className="sup-msg-info">Загрузка…</div></div></SupportShell>;
  }

  const isClosed = ticket.status === "closed" || ticket.status === "spam";

  return (
    <SupportShell>
      <div className="sup-page">
        <button type="button" className="sup-btn-ghost"
                onClick={() => navigate("/support/my")}
                style={{ marginBottom: 14 }}>
          ‹ К списку обращений
        </button>

        <div className="sup-chat">
          {/* Шапка */}
          <div className="sup-chat__head">
            <div className="sup-chat__head-title">
              #{ticket.id} · {ticket.subject}
            </div>
            <TicketStatusBadge status={ticket.status} />
            {!isClosed && (
              <div className="sup-chat__head-actions">
                <button type="button" className="sup-btn-ghost"
                        onClick={closeTicket}>
                  Закрыть обращение
                </button>
              </div>
            )}
          </div>

          {/* Чат */}
          <div className="sup-chat__body">
            <TicketChatView messages={ticket.messages || []} />
          </div>

          {/* Поле ответа */}
          <div className="sup-chat__compose">
            {isClosed ? (
              <div>
                <div className="sup-msg-info" style={{ marginBottom: 10 }}>
                  Обращение закрыто. Если проблема не решена — создай новое.
                </div>
                <button type="button" className="sup-btn-primary"
                        onClick={() => navigate("/support")}>
                  Новое обращение
                </button>
              </div>
            ) : (
              <>
                <textarea
                  className="sup-form__textarea"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder="Напиши ответ или уточнение…"
                  maxLength={4000}
                  rows={3}
                  disabled={sending}
                />
                <TicketAttachmentUpload
                  attachments={drafts}
                  onChange={setDrafts}
                />
                {sendErr && <div className="sup-msg-err">{sendErr}</div>}
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <button
                    type="button"
                    className="sup-btn-primary"
                    onClick={send}
                    disabled={sending || !draft.trim()}
                  >
                    <PaperAirplaneIcon size={16} />
                    {sending ? "Отправляем…" : "Отправить"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </SupportShell>
  );
}
